import { html, TemplateResult } from 'lit';
import { property, query } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from './GzFormFieldWebComponent';
import { FormFieldInput } from './FormFieldInput';
import { GzToggleWebComponent } from '../../../gzToggle';
import { FormEvent } from '../types/form-event.enum';
import { GzCheckboxWebComponent } from '../../../gzCheckbox';

export enum ToggleType {
  SWITCH = 'switch',
  CHECKBOX = 'checkbox',
}

export class GzFormFieldToggleableWebComponent<
  TInput extends FormFieldInput<T | undefined> = FormFieldInput<any>,
  T = unknown
> extends GzFormFieldWebComponent<TInput, T | undefined> {
  public disabledValue: T | undefined = undefined;

  @property({ type: Boolean, reflect: true, attribute: true })
  public isOn = false;

  /**
   * The input toggle component - gz-checkbox or gz-toggle(switch) - that can be used to enable/disable the input. Default is switch (gz-toggle).
   */
  @property({ type: String, reflect: true, attribute: true })
  public toggleType: ToggleType.SWITCH | ToggleType.CHECKBOX = ToggleType.SWITCH;

  @query('#form-field-toggle')
  private toggle:
    | GzFormFieldWebComponent<GzToggleWebComponent, boolean>
    | GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  private cachedDisabled: boolean | undefined = undefined;

  public set disabled(isDisabled: boolean) {
    this.cachedDisabled = isDisabled;

    if (this.toggle) {
      // disable the toggle
      this.toggle.disabled = isDisabled;
    }

    this.Input.setEnabled(!isDisabled && this.isOn);
  }

  public get disabled(): boolean {
    return this.toggle.disabled;
  }

  public set value(value: T | undefined) {
    if (value !== undefined) {
      this.setToggleFormValue(true);
    }

    super.value = value;
  }

  public get value() {
    return this.toggle.value ? this.Input.getFormValue() : this.disabledValue;
  }

  constructor() {
    super();

    if (this.cachedDisabled) {
      this.disabled = this.cachedDisabled;
    }
  }

  public setValueWithoutToggleUpdate(value: T | undefined) {
    super.value = value;
  }

  public validate(): void {
    if (this.isOn) {
      super.triggerValidation(true);
      return;
    }

    this.validSubject.next(true);
    this.Input.markAsValid();
  }

  protected async firstUpdated(changedProperties): Promise<void> {
    await super.firstUpdated(changedProperties);

    this.toggle.addEventListener(FormEvent.ValueChange, this.onToggleValueChange.bind(this));

    // for the first value (that doesn't trigger a change event)
    this.onToggleValueChange();
  }

  protected triggerAutoValidation(force = false) {
    if (this.isOn) {
      super.triggerAutoValidation(force);
      return;
    }

    this.resetField();
  }

  protected markAsInvalid(error: string) {
    if (!this.isOn) {
      this.validSubject.next(true);
      this.Input.markAsValid();
      return;
    }

    super.markAsInvalid(error);
  }

  private onToggleValueChange(event?: CustomEvent<{ value: boolean; isUserAction: boolean }>) {
    this.pristine = false;
    this.touched = true;
    this.isOn = this.toggle.value;
    const isUserAction = !!event?.detail.isUserAction;

    this.Input.setEnabled(!this.disabled && this.isOn);
    this.triggerAutoValidation(true);

    this.emitToggleableEvent(isUserAction);
  }

  private emitToggleableEvent(isUserAction: boolean): void {
    const toggleableChangeEvent = new CustomEvent<{ isOn: boolean; isUserAction: boolean }>(
      FormEvent.ToggleableChange,
      {
        detail: {
          isOn: this.isOn,
          isUserAction,
        },
        bubbles: true,
        composed: true,
      }
    );

    this.dispatchEvent(toggleableChangeEvent);
  }

  /**
   * @deprecated Use "disabled" instead.
   */
  public setEnabled(isEnabled: boolean): void {
    this.disabled = !isEnabled;
  }

  public setToggleFormValue(isChecked: boolean) {
    if (!this.toggle) {
      return;
    }

    (this.toggle as GzFormFieldWebComponent<GzToggleWebComponent, boolean>).value = isChecked;
    this.onToggleValueChange();
  }

  private renderToggleLabel() {
    return html`
      <span class="form-field-label toggle-label ${this.required ? 'required' : ''}" part="toggle-label">
        <gz-form-field id="form-field-toggle"> ${this.renderToggleElement()} </gz-form-field>
      </span>
    `;
  }

  private renderToggleElement(): TemplateResult {
    if (this.toggleType === ToggleType.CHECKBOX) {
      return html` <gz-checkbox
        gz-form-input
        ?checked="${this.isOn}"
        .label="${this.label ? this.label : ''}"
      ></gz-checkbox>`;
    }
    return html` <gz-toggle gz-form-input ?checked="${this.isOn}"> ${this.label ? this.label : ''} </gz-toggle>`;
  }

  public render() {
    return html`
      <div class="wrapper">
        ${this.renderToggleLabel()}
        <div class="input-wrapper" part="input-wrapper">
          <slot></slot>
        </div>
      </div>
    `;
  }
}
